import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { applyTheme } from './apply-theme';
import {
  DEFAULT_THEME_MODE,
  resolveThemeMode,
  type ResolvedTheme,
  type ThemeMode,
} from './theme-mode';

/**
 * Theme infrastructure: the light/dark mechanism, and nothing else.
 *
 * This is a presentation-infrastructure provider. It holds UI state only — the
 * requested theme mode — and it makes no domain conclusion, so RULE S-1 (a change
 * in UI state must not change a domain conclusion) holds trivially here: there is
 * no domain conclusion in scope.
 *
 * The mode is NOT persisted. Persisting it would be a storage binding, and FE1-W0
 * authorizes none. A later authorized increment may add one.
 */

interface ThemeContextValue {
  readonly mode: ThemeMode;
  readonly resolved: ResolvedTheme;
  readonly setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const DARK_QUERY = '(prefers-color-scheme: dark)';

function readSystemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(DARK_QUERY).matches;
}

export interface ThemeProviderProps {
  readonly children: ReactNode;
  /** Starting mode. Defaults to `SYSTEM`; not read from storage. */
  readonly initialMode?: ThemeMode;
}

export function ThemeProvider({ children, initialMode = DEFAULT_THEME_MODE }: ThemeProviderProps) {
  const [mode, setMode] = useState<ThemeMode>(initialMode);
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(readSystemPrefersDark);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent): void => {
      setSystemPrefersDark(event.matches);
    };
    query.addEventListener('change', onChange);
    return () => {
      query.removeEventListener('change', onChange);
    };
  }, []);

  const resolved = resolveThemeMode(mode, systemPrefersDark);

  useEffect(() => {
    applyTheme(document.documentElement, resolved);
  }, [resolved]);

  const value = useMemo<ThemeContextValue>(() => ({ mode, resolved, setMode }), [mode, resolved]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Read the active theme.
 *
 * Throws when used outside the provider. That is a frontend WIRING DEFECT, and it
 * is deliberately not softened into a default theme: silently falling back would
 * hide a composition error and is the same class of mistake as collapsing a
 * semantic state into a default.
 */
export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === null) {
    throw new Error(
      'useTheme() was called outside <ThemeProvider>. This is a frontend composition ' +
        'defect, not a read-model state.',
    );
  }
  return value;
}
